import { Box } from '@mui/material';
import Layout from '../../common/layout';
import PendingActionsFilter from './pending-actions-filter';
import PendingActionCard from './cards/pending-action-card-type-one';
import Loader from '../../common/loader';
import { useSelector } from 'react-redux';
import PendingActionCardTypeTwo from './cards/pending-action-card-type-two';
import {
  IMemberApprovalRequest,
  IMemberApprovalRequestData,
  IPendingActionTypeThreeData,
  IPendingActionTypeTwoData
} from '../type';
import PendingActionCardTypeThree from './cards/pending-action-card-type-three';
import { RootState } from '../../common/utils/root-state';
import PendingActionCardForReminderNotifications from './cards/pending-action-card-for-reminder-notifications';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_MEMBER_APPROVAL_REQUESTS } from '../gql';
import NoRecords from '../../common/application/no-records';

function PendingActions(): JSX.Element {
  const [resource, setResource] = useState<{
    displayName: string;
    id: string;
  } | null>(null);
  const [project, setProject] = useState<{
    projectName: string;
    id: string;
  } | null>(null);
  const memberId = useSelector(
    (state: RootState) => state.profileData.profileData?.memberId
  );

  const [pendingActions, setPendingActions] = useState<
    IMemberApprovalRequest[]
  >([]);
  const [skipCount, setSkipCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const prevFilters = useRef({
    resource,
    project
  });

  const prevSkip = useRef({
    skipCount
  });

  const { refetch, data: pendingActionsData } = useQuery(
    GET_MEMBER_APPROVAL_REQUESTS,
    {
      variables: {
        memberId: memberId ?? '',
        projectId: project?.id,
        resourceId: resource?.id,
        skip: 0
      }
    }
  );

  async function loadPendingActions(
    data: IMemberApprovalRequestData
  ): Promise<IMemberApprovalRequest[]> {
    if (!data?.getMemberApprovalRequests) {
      return [];
    }
    const fetchPendingActions = data.getMemberApprovalRequests;
    const modifiedPendingActions: IMemberApprovalRequest[] = [];

    fetchPendingActions.forEach((pendingAction) => {
      const pendingActionCopy = JSON.parse(JSON.stringify(pendingAction));
      const metadata = JSON.parse(pendingActionCopy.approvalRequest.metadata);
      pendingActionCopy.approvalRequest.metadata = metadata;

      modifiedPendingActions.push(pendingActionCopy);
    });
    return modifiedPendingActions;
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (pendingActionsData) {
        const fetchPendingActions = await loadPendingActions(
          pendingActionsData
        );

        const filtersChanged =
          resource?.id !== prevFilters.current.resource?.id ||
          project?.id !== prevFilters.current.project?.id;

        if (filtersChanged) {
          setPendingActions(fetchPendingActions);
          prevFilters.current = { resource, project };
          setIsLoading((state) => !state);
        } else if (skipCount !== prevSkip.current.skipCount) {
          setPendingActions((prevPendingActions) => [
            ...prevPendingActions,
            ...fetchPendingActions
          ]);
        } else {
          setPendingActions(fetchPendingActions);
          setIsLoading(true);
          await refetch();
          setIsLoading(false);
        }
      }
    };
    void fetchData();
  }, [pendingActionsData, resource, project]);

  useEffect(() => {
    setIsLoading((state) => !state);
  }, [resource, project]);

  const handleScroll = (event: React.UIEvent<HTMLElement>): void => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 1) {
      const currentLength = pendingActions.length;
      void handleLoadMore(currentLength);
      setSkipCount(currentLength);
    }
  };

  const handleLoadMore = async (skip: number): Promise<void> => {
    await refetch({
      skip
    });
  };

  return (
    <Layout
      mainHeading="Pending Actions"
      subHeading="User Management > Pending Actions"
    >
      <PendingActionsFilter
        setResourceData={(
          value: {
            displayName: string;
            id: string;
          } | null
        ) => setResource(value)}
        setProjectData={(
          value: {
            projectName: string;
            id: string;
          } | null
        ) => setProject(value)}
      />
      <Box
        sx={{
          width: '100%',
          overflowY: 'auto',
          height: 'calc(100vh - 150px)',
          '&::-webkit-scrollbar': {
            width: '0.4em'
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.2)',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '10px'
          },
          paddingRight: '5px'
        }}
        onScroll={handleScroll}
      >
        {isLoading && pendingActions.length === 0 ? (
          <Loader />
        ) : pendingActions.length !== 0 ? (
          <>
            {pendingActions.map((action: IMemberApprovalRequest) => {
              if (action.approvalRequest.type === 'REMOVE_MEMBER') {
                return (
                  <PendingActionCard
                    key={action.id}
                    id={action.id}
                    date={new Date(action.createdAt)}
                    resources={action.resourcesArray ?? []}
                    description={
                      action.approvalRequest.description?.toString() ?? ''
                    }
                    state={action.state}
                  />
                );
              } else if (
                action.approvalRequest.type === 'PROJECT_TIMELINE_CHANGE' ||
                action.approvalRequest.type === 'PROJECT_STATUS_CHANGE' ||
                action.approvalRequest.type === 'MEMBER_SKILL_ADD' ||
                action.approvalRequest.type === 'MEMBER_SKILL_UPDATE'
              ) {
                return (
                  <PendingActionCardTypeTwo
                    key={action.id}
                    cardData={action as IPendingActionTypeTwoData}
                  />
                );
              } else if (
                action.approvalRequest.type === 'ADD_MEMBER' ||
                action.approvalRequest.type === 'MEMBER_ALLOCATION_CHANGE'
              ) {
                return (
                  <PendingActionCardTypeThree
                    key={action.id}
                    cardData={action as IPendingActionTypeThreeData}
                  />
                );
              } else if (
                action.approvalRequest.type === 'GENERATED_STATUS_CHANGE'
              ) {
                return (
                  <PendingActionCardForReminderNotifications
                    key={action.id}
                    id={action.id}
                    description={
                      action.approvalRequest.description?.toString() ?? ''
                    }
                    date={new Date(action.createdAt)}
                    newStatus={
                      action.approvalRequest.metadata.newStatus as string
                    }
                    state={action.state}
                  />
                );
              }
              return null;
            })}
          </>
        ) : (
          <NoRecords message={'No Pending Actions Available'} />
        )}
      </Box>
    </Layout>
  );
}

export default PendingActions;
